
import { useLocalStorage as useLocalStorageHook } from "usehooks-ts";

const useLocalStorage = <T>(...args: Parameters<typeof useLocalStorageHook<T>>) => {
  const [key, initialValue, options] = args;
  return useLocalStorageHook<T>(key, initialValue, {
    serializer: (value: any) => {
      if (typeof value === "string") {
        return value;
      }
      
      return JSON.stringify(value);
    },
    deserializer: (value: any) => {
      // If value is not a string, return as-is
      if (typeof value !== "string") {
        return value;
      }

      // Handle empty string
      if (value === "") {
        return value;
      }

      // Try to parse as JSON first
      try {
        const parsed = JSON.parse(value);
        
        // If the parsed value is a string, check if it's doubly serialized
        if (typeof parsed === "string") {
          try {
            // Try to parse again in case it's doubly serialized
            return JSON.parse(parsed);
          } catch {
            // If second parse fails, return the first parsed string
            return parsed;
          }
        }
        
        return parsed;
      } catch (error) {
        // If JSON.parse fails, check for primitive types
        
        // Handle boolean strings
        if (value === "true") return true;
        if (value === "false") return false;
        
        // Handle number strings
        if (!isNaN(Number(value)) && value.trim() !== "") {
          const num = Number(value);
          // Check if it's an integer or float
          if (Number.isInteger(num)) {
            return parseInt(value, 10);
          } else {
            return parseFloat(value);
          }
        }
        
        // If all else fails, return the original string
        return value;
      }
    },
    ...(options || {})
  });
};

export default useLocalStorage;
